import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Dropdown, DropdownToggle, DropdownMenu } from "reactstrap"
import SimpleBar from "simplebar-react"
import {
  FaBell,
  FaExclamationTriangle,
  FaFileInvoiceDollar,
  FaPaperPlane,
  FaDownload,
  FaFileExcel,
  FaRegClock,
} from "react-icons/fa"
import { withTranslation } from "react-i18next"
import Configs from "../../../config"
import Swal from "sweetalert2"
import { fetchData } from "utils/Common_api_functions"

const NotificationDropdown = props => {
  const [menu, setMenu] = useState(false)
  const [notificationsData, setNotificationsData] = useState([])

  useEffect(() => {
    fetchSheetHistory()
  }, [])

  async function fetchSheetHistory() {
    const APIdata = await fetchData(() => {}, "logs/sheet")
    if (APIdata?.success) {
      setNotificationsData(APIdata?.data)
    }
  }

  const sendNotification = notification => {
    Swal.fire({
      icon: "success",
      title: "Notification Sent Successfully",
    })
  }

  const handleDownload = notification => {
    if (!notification?.sheetLink) {
      Swal.fire({
        icon: "error",
        title: "File Not Found",
        text: "No file available to download.",
      })
      return
    }

    const downloadUrl = `${Configs.imageUrl}${notification.sheetLink.replace(
      /\\/g,
      "/"
    )}`

    const link = document.createElement("a")
    link.href = downloadUrl
    link.download = notification.fileName || "download"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const formatDate = dateStr => {
    if (!dateStr) return ""
    const date = new Date(dateStr)
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getTypeLabel = type => {
    switch (type?.toLowerCase()) {
      case "kpi":
        return "KPI Sheet"
      case "missing":
        return "Missing Log"
      case "pending":
        return "Pending Task"
      default:
        return type || "File"
    }
  }

  const getTypeIcon = type => {
    switch (type?.toLowerCase()) {
      case "kpi":
        return <FaFileExcel />
      case "missing":
        return <FaExclamationTriangle />
      case "pending":
        return <FaFileInvoiceDollar />
      default:
        return <FaBell />
    }
  }

  const getTypeTheme = type => {
    switch (type?.toLowerCase()) {
      case "kpi":
        return { bg: "#e8f7ee", color: "#1e8e4e" }
      case "missing":
        return { bg: "#fff4e5", color: "#b26a00" }
      case "pending":
        return { bg: "#e5f2ff", color: "#0b6bc2" }
      default:
        return { bg: "#f1f5f9", color: "#64748b" }
    }
  }

  return (
    <Dropdown
      isOpen={menu}
      toggle={() => setMenu(!menu)}
      className="dropdown d-inline-block"
      tag="li"
    >
      <DropdownToggle
        className="btn header-item noti-icon position-relative"
        tag="button"
        id="page-header-notifications-dropdown"
      >
        <i className="bx bx-bell bx-tada text-white" />
      </DropdownToggle>

      <DropdownMenu
        className="dropdown-menu dropdown-menu-end p-0"
        style={{ width: "420px", maxWidth: "92vw" }}
      >
        {/* Header */}
        <div
          className="d-flex align-items-center justify-content-between px-3 py-3"
          style={{
            background: "linear-gradient(135deg, #556ee6 0%, #34c38f 100%)",
          }}
        >
          <div className="d-flex align-items-center gap-2 text-white">
            <FaBell size={16} />
            <h6 className="m-0 fw-semibold">{props.t("Sheet History")}</h6>
          </div>
          {notificationsData.length > 0 && (
            <span
              className="badge rounded-pill"
              style={{ background: "rgba(255,255,255,0.25)", color: "#fff" }}
            >
              {notificationsData.length}
            </span>
          )}
        </div>

        {/* List */}
        <SimpleBar style={{ maxHeight: "320px" }}>
          {notificationsData.length === 0 ? (
            <div className="text-center py-5">
              <div
                className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle"
                style={{
                  width: "56px",
                  height: "56px",
                  background: "#f1f5f9",
                }}
              >
                <FaBell size={22} style={{ color: "#94a3b8" }} />
              </div>
              <p className="mb-0 font-size-13 text-muted">No notifications</p>
            </div>
          ) : (
            notificationsData.map((notification, index) => {
              const theme = getTypeTheme(notification.type)
              return (
                <div
                  key={notification.id ?? index}
                  onClick={() => {}}
                  className="notification-row d-flex align-items-start justify-content-between px-3 py-3"
                  style={{
                    borderBottom: "1px solid #f1f5f9",
                    cursor: "default",
                    transition: "background-color 0.15s ease",
                  }}
                  onMouseEnter={e =>
                    (e.currentTarget.style.backgroundColor = "#f8fafc")
                  }
                  onMouseLeave={e =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  {/* Left: Icon + Content */}
                  <div
                    className="d-flex align-items-start flex-grow-1 me-2"
                    style={{ minWidth: 0 }}
                  >
                    {/* Type Icon */}
                    <div
                      className="flex-shrink-0 d-flex align-items-center justify-content-center me-3"
                      style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "10px",
                        background: theme.bg,
                        color: theme.color,
                        fontSize: "16px",
                      }}
                    >
                      {notification.icon ?? getTypeIcon(notification.type)}
                    </div>

                    {/* Title / Date / Badge */}
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <h6
                        className="mb-1 fw-semibold"
                        style={{
                          fontSize: "13.5px",
                          color: "#212529",
                          wordBreak: "break-word",
                        }}
                      >
                        {notification.title ??
                          notification.fileName ??
                          "File Upload"}
                      </h6>

                      <div
                        className="d-flex align-items-center gap-1 mb-2"
                        style={{ fontSize: "11.5px", color: "#94a3b8" }}
                      >
                        <FaRegClock size={10} />
                        <span>{formatDate(notification.createdAt)}</span>
                      </div>

                      <span
                        className="badge"
                        style={{
                          background: theme.bg,
                          color: theme.color,
                          fontSize: "10.5px",
                          fontWeight: 600,
                          padding: "4px 10px",
                          borderRadius: "20px",
                          textTransform: "capitalize",
                        }}
                      >
                        {getTypeLabel(notification.type)}
                      </span>
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div
                    className="d-flex flex-column gap-2 flex-shrink-0"
                    style={{ marginTop: "2px" }}
                  >
                    {notification.sheetLink && (
                      <button
                        className="btn btn-sm d-flex align-items-center justify-content-center border-0"
                        title={`Download ${notification.fileName || "file"}`}
                        onClick={e => {
                          e.stopPropagation()
                          handleDownload(notification)
                        }}
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "8px",
                          background: "#e8f7ee",
                          color: "#1e8e4e",
                        }}
                      >
                        <FaDownload size={13} />
                      </button>
                    )}

                    {(notification.type === "missing" ||
                      notification.type === "pending") && (
                      <button
                        className="btn btn-sm d-flex align-items-center justify-content-center border-0"
                        title="Send Notification"
                        onClick={e => {
                          e.stopPropagation()
                          sendNotification(notification)
                        }}
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "8px",
                          background: "#e5f2ff",
                          color: "#0b6bc2",
                        }}
                      >
                        <FaPaperPlane size={13} />
                      </button>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </SimpleBar>
      </DropdownMenu>
    </Dropdown>
  )
}

NotificationDropdown.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(NotificationDropdown)
